import type { Resolvable } from '@/services/resolvers/types'
import TmLogicError from '@/core/error/tmLogicError'

export function persistent() {
  return function <T extends { new (...args: any[]): Resolvable }>(constructor: T) {
    // add isPersistent method to prototype instead of creating new class
    constructor.prototype.isPersistent = function () {
      return true
    }

    // create proxy for constructor that adds verification
    const proxiedConstructor = new Proxy(constructor, {
      construct(Target, args) {
        const instance = new Target(...args)

        if (typeof instance.getResolverKeys !== 'function') {
          throw new TmLogicError('Persistent resolver requires "getResolverKeys" method to be implemented')
        }

        return instance
      },

      get(target, prop) {
        // save original class name
        if (prop === 'name') {
          return constructor.name
        }
        // save original prototype
        if (prop === 'prototype') {
          return constructor.prototype
        }
        return target[prop]
      },
    })

    // copy all metadata from original constructor to proxy
    const metadataKeys = Reflect.getMetadataKeys?.(constructor) || []
    metadataKeys.forEach((key) => {
      const metadata = Reflect.getMetadata(key, constructor)
      Reflect.defineMetadata(key, metadata, proxiedConstructor)
    })

    // copy prototype metadata
    const prototypeMetadataKeys = Reflect.getMetadataKeys?.(constructor.prototype) || []
    prototypeMetadataKeys.forEach((key) => {
      const metadata = Reflect.getMetadata(key, constructor.prototype)
      Reflect.defineMetadata(key, metadata, proxiedConstructor.prototype)
    })

    // return proxy that saves all metadata
    return proxiedConstructor
  }
}
